import { Flex, Group } from '@mantine/core';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RealTimeEvents } from '@e-origin/shared';

import { Logo } from '@components';
import { useProgress } from '@hooks/ProgressContext';
import { queryClient } from '@hooks/queryClient';
import { useSettings } from '@hooks/useSettings';
import { useWebSocket } from '@hooks/useWebsocket';
import classes from './Header.module.css';
import { ReportMenu } from './ReportMenu';
import { UserMenu } from './UserMenu';

export const Header = () => {
  const { onMessage } = useWebSocket();
  const { setProgressForBatch } = useProgress();
  const { data: settings } = useSettings();

  // eslint-disable-next-line max-statements
  const handleMessage = (payload) => {
    const message: { event: RealTimeEvents; data: any } = JSON.parse(payload);
    if (message.event === RealTimeEvents.BATCH_UPDATE_RESULT && message.data?.error) {
      toast.error('Something went wrong!');
    }

    if (
      (message.event === RealTimeEvents.FILE_PROCESS_FAILED ||
        message.event === RealTimeEvents.AI_FILE_MODIFIED ||
        message.event === RealTimeEvents.SENDING_FAILED) &&
      message.data
    ) {
      toast.error(message.data ?? 'Something went wrong!', { autoClose: 30000, closeOnClick: false });
    }

    if (
      [
        RealTimeEvents.FILE_UPLOAD_RESULT,
        RealTimeEvents.BATCH_SEND_ALL_FINISHED,
        RealTimeEvents.BATCH_SYNC_ALL_FINISHED,
        RealTimeEvents.BATCH_UPDATE_RESULT,
        RealTimeEvents.STARTING_TO_SEND,
        RealTimeEvents.REFRESH_BATCHES,
      ].includes(message.event) ||
      (message.event === RealTimeEvents.SCRAPING_PROGRESS && message.data?.value === 100)
    ) {
      queryClient.invalidateQueries({ queryKey: ['batches'] });
    }

    if (message.event === RealTimeEvents.REFRESH_REPORTS) {
      queryClient.invalidateQueries({ queryKey: ['getUserReports'] });
    }

    if (message.event === RealTimeEvents.REFRESH_CUSTOMS_REPORTS) {
      queryClient.invalidateQueries({ queryKey: ['batches-customs-report'] });
    }

    if (message.data && ![RealTimeEvents.REFRESH_BATCHES, RealTimeEvents.BATCH_SEND_ALL_FINISHED].includes(message.event)) {
      setProgressForBatch(message.data);
    }

    if (message.event === RealTimeEvents.REFRESH_DECLARANT) {
      queryClient.invalidateQueries({ queryKey: ['declarant'] });
    }

    if (message.event === RealTimeEvents.REFRESH_CUSTOMER && message.data?.customerId) {
      queryClient.invalidateQueries({ queryKey: ['customer', message.data.customerId] });
    }

    if (message.event === RealTimeEvents.REFRESH_TRANSITS) {
      queryClient.invalidateQueries({ queryKey: ['transits'] });

      if (message.data?.transitId) {
        queryClient.invalidateQueries({ queryKey: ['transit', message.data.transitId] });
      }
    }
  };

  useEffect(() => {
    onMessage('Main Listener', (message) => handleMessage(message));
  }, []);

  const badgeDetails = settings?.isForwarder ? { badge: 'Customs Trade', badgeColor: '#ECCC38' } : { badge: 'Customs Clear', badgeColor: '#C0E5C9' };

  return (
    <Flex className={classes.headerContainer}>
      <Flex className={classes.logoContainer}>
        <Link to="/">
          <Logo badge={badgeDetails.badge} badgeColor={badgeDetails.badgeColor} />
        </Link>
      </Flex>
      <Group>
        <ReportMenu />
        <UserMenu />
      </Group>
    </Flex>
  );
};
